import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, GraduationCap, ArrowRight, X } from 'lucide-react';
import { useAuth } from '../App';

/**
 * Glass-overlay modal shown when a guest visitor reaches a gated page
 * (e.g. /courses, /universities). The page beneath stays visible but
 * blurred + non-interactive. Two CTAs: Sign In and Register.
 *
 * Usage:
 *   const { user, loading } = useAuth();
 *   ...
 *   return (
 *     <PublicLayout>
 *       <div className={!user ? "blur-sm pointer-events-none select-none" : ""}>...</div>
 *       {!user && !loading && <LoginGate onClose={() => navigate('/')} />}
 *     </PublicLayout>
 *   );
 */
const LoginGate = ({ title = 'Sign in to see your results', subtitle = 'EduBridge Network personalises every search based on your profile. Create a free account or sign in to view this page.', returnTo, onClose }) => {
  const navigate = useNavigate();
  const goLogin = () => {
    const url = returnTo ? `/login?next=${encodeURIComponent(returnTo)}` : '/login';
    navigate(url);
  };
  const goRegister = () => {
    const url = returnTo ? `/register?as=student&next=${encodeURIComponent(returnTo)}` : '/register?as=student';
    navigate(url);
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#1A2A4F]/40 backdrop-blur-sm" data-testid="login-gate-overlay" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {onClose && (
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100" aria-label="Close" data-testid="login-gate-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
        <div className="bg-gradient-to-br from-[#1A2A4F] to-[#2d4373] px-7 py-6 text-white">
          <div className="w-12 h-12 rounded-xl bg-[#F5A623] flex items-center justify-center shadow-lg shadow-[#F5A623]/30">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <h2 className="mt-4 text-xl font-bold">{title}</h2>
          <p className="mt-1.5 text-sm opacity-85">{subtitle}</p>
        </div>
        <div className="p-7 space-y-3">
          <button
            onClick={goRegister}
            className="w-full bg-[#F5A623] text-white font-bold py-3 rounded-lg hover:brightness-95 transition flex items-center justify-center gap-2 shadow-lg shadow-[#F5A623]/30"
            data-testid="login-gate-register-btn"
          >
            <GraduationCap className="w-4 h-4" /> Create a free account
          </button>
          <button
            onClick={goLogin}
            className="w-full border border-[#1A2A4F]/15 text-[#1A2A4F] font-bold py-3 rounded-lg hover:bg-gray-50 transition flex items-center justify-center gap-2"
            data-testid="login-gate-login-btn"
          >
            I already have an account <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-[11px] text-center text-gray-400 pt-2">
            We never share your data. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginGate;
